import React from "react";
import { func, number, string } from "prop-types";
import { Card, Heading, Separator } from "uikit/components";
import styles from "./SchoolIndicatorCard.module.styl";
import Chart from "react-apexcharts";
import cx from "classnames";
import IconTooltip from "uikit/components/IconTooltip/IconTooltip";
import svgIcons from "uikit/assets/svgIcons";
import { percentageLevels } from "utils/indicator/indicatorSettings.js";
import { chartOptions, smallChartWidth } from "utils/chart/chartOptions";

export default function SchoolIndicatorCard({
    title,
    percentage,
    numerator,
    denominator,
    info,
    descriptionTitle,
    descriptionText,
    footerGoodValueText,
    footerBadValueText,
    footerBadValueLinkUrl,
    footerBadValueLinkText,
    instructionUrl,
    onClick,
    dataTestName,
    additionalLinkText,
    additionalLinkOnClick,
    additionalLinkDataTestName,
    footerBadValueLinkUrlDataTestName,
    instructionLinkDataTestName,
}) {
    const onChildClick = function (event) {
        event.stopPropagation();
    };

    const indicatorStyle = percentageLevels.indicatorColor(percentage);
    const options = chartOptions(indicatorStyle);
    const series = [percentage];

    const description =
        descriptionText != null && denominator != null
            ? `${descriptionText}: ${denominator - numerator}`
            : "";

    return (
        <Card
            borderRadius={"giant"}
            padding={"none"}
            className={cx(styles.form, onClick && styles.pointer)}
            onClick={onClick}
            borderColor={percentageLevels.indicatorColor(percentage).colorName}
        >
            <div data-test-name={dataTestName}>
                <div>
                    <div className={styles.heading}>
                        <Heading size={"h2"}>{title}</Heading>
                        <div className={styles.icon}>
                            <IconTooltip trigger="hover">
                                <div className={styles.infoPanel}>{info}</div>
                            </IconTooltip>
                        </div>
                    </div>
                    <div className={styles.indicators}>
                        <div className={styles.numerator}>{numerator}</div>
                        {denominator != null && (
                            <div className={styles.denominator}>/{denominator}</div>
                        )}
                    </div>
                    <Heading size={"h3"} className={styles.descriptionTitle}>
                        {descriptionTitle}
                    </Heading>
                    <div className={styles.descriptionText}>{description}</div>
                </div>

                <div className={styles.chart}>
                    {additionalLinkText !== null && additionalLinkOnClick !== null && (
                        <div
                            className={cx(styles.link, styles.additionalLink, styles.pointer)}
                            onClick={additionalLinkOnClick}
                            data-test-name={additionalLinkDataTestName}
                        >
                            {additionalLinkText}
                        </div>
                    )}
                    <Chart
                        options={options}
                        series={series}
                        type="radialBar"
                        width={smallChartWidth}
                    />
                </div>
            </div>
            <Separator />
            <div className={styles.footer} onClick={onChildClick}>
                {percentage >= percentageLevels.good ? (
                    <div className={styles.descriptionText}>{footerGoodValueText}</div>
                ) : (
                    <div className={styles.descriptionText}>
                        <div className={styles.badValue}>
                            <div className={styles.footerBadValueText}>
                                <div className={styles.badValueSign}>{svgIcons.badValueSign}</div>
                                {footerBadValueText}
                                <a
                                    className={styles.link}
                                    rel="noreferrer"
                                    target="_blank"
                                    href={footerBadValueLinkUrl}
                                    data-test-name={footerBadValueLinkUrlDataTestName}
                                >
                                    {footerBadValueLinkText}
                                </a>
                            </div>
                        </div>
                        <div className={styles.instruction}>
                            <a
                                className={styles.link}
                                rel="noreferrer"
                                target="_blank"
                                href={instructionUrl}
                                data-test-name={instructionLinkDataTestName}
                            >
                                Инструкция
                            </a>
                            <div className={styles.instructionText}>на портале поддержки.</div>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
}

SchoolIndicatorCard.propTypes = {
    title: string.isRequired,
    percentage: number.isRequired,
    numerator: number.isRequired,
    denominator: number,
    info: string.isRequired,
    descriptionTitle: string.isRequired,
    descriptionText: string,
    footerGoodValueText: string.isRequired,
    footerBadValueText: string.isRequired,
    footerBadValueLinkUrl: string.isRequired,
    footerBadValueLinkText: string.isRequired,
    instructionUrl: string.isRequired,
    onClick: func,
    additionalLinkText: string,
    additionalLinkOnClick: func,
    additionalLinkDataTestName: string,
    dataTestName: string,
    footerBadValueLinkUrlDataTestName: string,
    instructionLinkDataTestName: string,
};
